import { Coordinate } from '@/types/floorMap/Coodinate'

/**
 * ドラクエ9 宝の地図フロアマップの領域内に生成された部屋の情報を保持するクラス
 * @class Room
 */
class Room {
  /**
   * @type {number} 部屋の左端のx座標
   */
  private _left: number = 0

  /**
   * @type {number} 部屋の上端のy座標
   */
  private _top: number = 0

  /**
   * @type {number} 部屋の右端のx座標
   */
  private _right: number = 0

  /**
   * @type {number} 部屋の下端のy座標
   */
  private _bottom: number = 0

  /**
   * @type {Coordinate} 左端上のノードの座標
   */
  private _leftmostTop: Coordinate = { x: 0, y: 0 }

  /**
   * @type {Coordinate} 左端下のノードの座標
   */
  private _leftmostBottom: Coordinate = { x: 0, y: 0 }

  /**
   * @type {Coordinate} 右端上のノードの座標
   */
  private _rightmostTop: Coordinate = { x: 0, y: 0 }

  /**
   * @type {Coordinate} 右端下のノードの座標
   */
  private _rightmostBottom: Coordinate = { x: 0, y: 0 }

  /**
   * @type {Coordinate} 上端左のノードの座標
   */
  private _topmostLeft: Coordinate = { x: 0, y: 0 }

  /**
   * @type {Coordinate} 上端右のノードの座標
   */
  private _topmostRight: Coordinate = { x: 0, y: 0 }

  /**
   * @type {Coordinate} 下端左のノードの座標
   */
  private _bottommostLeft: Coordinate = { x: 0, y: 0 }

  /**
   * @type {Coordinate} 下端右のノードの座標
   */
  private _bottommostRight: Coordinate = { x: 0, y: 0 }

  /**
   * 部屋の左端のx座標のgetter
   * @returns {number} 部屋の左端のx座標
   */
  public get left(): number {
    return this._left
  }

  /**
   * 部屋の左端のx座標のsetter
   * @param {number} value 部屋の左端のx座標
   */
  public set left(value: number) {
    this._left = value
  }

  /**
   * 部屋の上端のy座標のgetter
   * @returns {number} 部屋の上端のy座標
   */
  public get top(): number {
    return this._top
  }

  /**
   * 部屋の上端のy座標のsetter
   * @param {number} value 部屋の上端のy座標
   */
  public set top(value: number) {
    this._top = value
  }

  /**
   * 部屋の右端のx座標のgetter
   * @returns {number} 部屋の右端のx座標
   */
  public get right(): number {
    return this._right
  }

  /**
   * 部屋の右端のx座標のsetter
   * @param {number} value 部屋の右端のx座標
   */
  public set right(value: number) {
    this._right = value
  }

  /**
   * 部屋の下端のy座標のgetter
   * @returns {number} 部屋の下端のy座標
   */
  public get bottom(): number {
    return this._bottom
  }

  /**
   * 部屋の下端のy座標のsetter
   * @param {number} value 部屋の下端のy座標
   */
  public set bottom(value: number) {
    this._bottom = value
  }

  /**
   * 左端上のノードの座標のgetter
   * @returns {Coordinate} 左端上のノードの座標
   */
  public get leftmostTop(): Coordinate {
    return { ...this._leftmostTop }
  }

  /**
   * 左端上のノードの座標のsetter
   * @param {Coordinate} value 左端上のノードの座標
   */
  public set leftmostTop(value: Coordinate) {
    this._leftmostTop = { ...value }
  }

  /**
   * 左端下のノードの座標のgetter
   * @returns {Coordinate} 左端下のノードの座標
   */
  public get leftmostBottom(): Coordinate {
    return { ...this._leftmostBottom }
  }

  /**
   * 左端下のノードの座標のsetter
   * @param {Coordinate} value 左端下のノードの座標
   */
  public set leftmostBottom(value: Coordinate) {
    this._leftmostBottom = { ...value }
  }

  /**
   * 右端上のノードの座標のgetter
   * @returns {Coordinate} 右端上のノードの座標
   */
  public get rightmostTop(): Coordinate {
    return { ...this._rightmostTop }
  }

  /**
   * 右端上のノードの座標のsetter
   * @param {Coordinate} value 右端上のノードの座標
   */
  public set rightmostTop(value: Coordinate) {
    this._rightmostTop = { ...value }
  }

  /**
   * 右端下のノードの座標のgetter
   * @returns {Coordinate} 右端下のノードの座標
   */
  public get rightmostBottom(): Coordinate {
    return { ...this._rightmostBottom }
  }

  /**
   * 右端下のノードの座標のsetter
   * @param {Coordinate} value 右端下のノードの座標
   */
  public set rightmostBottom(value: Coordinate) {
    this._rightmostBottom = { ...value }
  }

  /**
   * 上端左のノードの座標のgetter
   * @returns {Coordinate} 上端左のノードの座標
   */
  public get topmostLeft(): Coordinate {
    return { ...this._topmostLeft }
  }

  /**
   * 上端左のノードの座標のsetter
   * @param {Coordinate} value 上端左のノードの座標
   */
  public set topmostLeft(value: Coordinate) {
    this._topmostLeft = { ...value }
  }

  /**
   * 上端右のノードの座標のgetter
   * @returns {Coordinate} 上端右のノードの座標
   */
  public get topmostRight(): Coordinate {
    return { ...this._topmostRight }
  }

  /**
   * 上端右のノードの座標のsetter
   * @param {Coordinate} value 上端右のノードの座標
   */
  public set topmostRight(value: Coordinate) {
    this._topmostRight = { ...value }
  }

  /**
   * 下端左のノードの座標のgetter
   * @returns {Coordinate} 下端左のノードの座標
   */
  public get bottommostLeft(): Coordinate {
    return { ...this._bottommostLeft }
  }

  /**
   * 下端左のノードの座標のsetter
   * @param {Coordinate} value 下端左のノードの座標
   */
  public set bottommostLeft(value: Coordinate) {
    this._bottommostLeft = { ...value }
  }

  /**
   * 下端右のノードの座標のgetter
   * @returns {Coordinate} 下端右のノードの座標
   */
  public get bottommostRight(): Coordinate {
    return { ...this._bottommostRight }
  }

  /**
   * 下端右のノードの座標のsetter
   * @param {Coordinate} value 下端右のノードの座標
   */
  public set bottommostRight(value: Coordinate) {
    this._bottommostRight = { ...value }
  }

  /**
   * 部屋の4辺の座標を一括で指定する
   * @param {number} left 部屋の左端のx座標
   * @param {number} top 部屋の上端のy座標
   * @param {number} right 部屋の右端のx座標
   * @param {number} bottom 部屋の下端のy座標
   */
  public setCoordinate(left: number, top: number, right: number, bottom: number): void {
    this._left = left
    this._top = top
    this._right = right
    this._bottom = bottom
  }
}

export { Room }
